import {
	DEFAULT_MEDICAL_TITLE,
	parseMedicalTitle,
} from 'App/Components/TitleSelector/TitleSelector';
import { DEFAULT_FORM_VALUES } from './DoctorDrawer.constants';
import { Doctor, DoctorFormValues, DoctorOption } from './DoctorDrawer.types';

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}/;
const SLASHED_DATE_PATTERN = /^(\d{2})\/(\d{2})\/(\d{4})$/;

/**
 * O DatePicker do design system só renderiza datas no formato `YYYY-MM-DD`,
 * então datas com hora (`1994-10-29T00:00:00.000Z`) apareceriam em branco.
 */
export const toFormDate = (value?: string | null): string => {
	if (!value) return '';

	const [isoDate] = ISO_DATE_PATTERN.exec(value) ?? [];
	if (isoDate) return isoDate;

	const slashedDate = SLASHED_DATE_PATTERN.exec(value);
	if (slashedDate) {
		const [, day, month, year] = slashedDate;
		return `${year}-${month}-${day}`;
	}

	return '';
};

/** Mantém o código `0`, que é uma entidade federativa válida. */
export const toFormCode = (value?: number | string | null): string => {
	if (value === null || value === undefined || value === '') return '';

	return String(value);
};

/** Códigos são exibidos como string no form e enviados como número na request. */
export const toRequestCode = (value: string): number | undefined => {
	if (value === '') return undefined;

	return Number(value);
};

/** Aceita tanto `[{ id, name }]` quanto `['id']`. */
export const toOptionList = (value?: unknown): DoctorOption[] => {
	if (!Array.isArray(value)) return [];

	return value
		.map((item) => (typeof item === 'string' ? { id: item } : item))
		.filter((item): item is DoctorOption => Boolean(item?.id));
};

export const mapDoctorToFormValues = (user: Doctor): DoctorFormValues => ({
	...DEFAULT_FORM_VALUES,
	title: parseMedicalTitle(user.title) || DEFAULT_MEDICAL_TITLE,
	firstName: user.firstName || user.name || '',
	middleName: user.middleName || '',
	lastName: user.lastName || '',
	hasNoMiddleName: user.hasNoMiddleName ?? !user.middleName?.trim(),
	hasNoLastName: user.hasNoLastName ?? !user.lastName?.trim(),
	birthDate: toFormDate(user.birthDate),
	sex: user.sex || '',
	contactNumber: user.contactNumber || '',
	countryCode: toFormCode(user.countryCode),
	federalEntityCode: toFormCode(user.federalEntityCode),
	nationalityCode: toFormCode(user.nationalityCode),
	nationalIdentity: user.nationalIdentity || '',
	countryResidenceCode: toFormCode(user.countryResidenceCode),
	federalEntityResidenceCode: toFormCode(user.federalEntityResidenceCode),
	municipalityResidenceCode: toFormCode(user.municipalityResidenceCode),
	localityResidenceCode: toFormCode(user.localityResidenceCode),
	professionalLicenseId: user.professionalLicenseId || '',
	university: user.university || '',
	doctorPersonalTypeId:
		user.doctorPersonalTypes?.id || user.doctorPersonalTypeId || '',
	specialties: toOptionList(user.specialties ?? user.specialtyIds),
	branchOffices: [],
	email: user.email || '',
});
